// Carrier library types — FE-SCRM-030.
//
// Mirror the BE-SCRM-030 enums (``CarrierType``, ``CarrierStatus``,
// ``CarrierContactRoleType``, ``CarrierContactStatus``) so the FE
// stays in step with the backend without a runtime fetch of enum
// values. The backend is the source of truth; if it gains a new
// enum value (e.g. a new ``CarrierType``), update here in lockstep.

export const CARRIER_TYPES = [
  "medical",
  "dental",
  "vision",
  "life",
  "disability",
  "supplemental",
  "pharmacy",
  "multi_line",
] as const;
export type CarrierType = (typeof CARRIER_TYPES)[number];


export const CARRIER_STATUSES = ["active", "inactive"] as const;
export type CarrierStatus = (typeof CARRIER_STATUSES)[number];


export const CARRIER_CONTACT_ROLE_TYPES = [
  "sales_rep",
  "account_manager",
  "underwriter",
  "implementation",
  "claims",
  "billing",
  "broker_relations",
  "compliance",
  "other",
] as const;
export type CarrierContactRoleType =
  (typeof CARRIER_CONTACT_ROLE_TYPES)[number];


export const CARRIER_CONTACT_STATUSES = [
  "active",
  "inactive",
  "left_carrier",
] as const;
export type CarrierContactStatus =
  (typeof CARRIER_CONTACT_STATUSES)[number];


export interface CarrierListRow {
  carrier_id: string;
  name: string;
  normalized_name: string;
  carrier_type: CarrierType;
  naic_code: string | null;
  parent_carrier_id: string | null;
  website: string | null;
  logo_url: string | null;
  status: CarrierStatus;
  notes: string | null;
  /** Active CarrierContacts at this carrier (BE aggregate; 0 when
   * none / when serialized off the non-list code path). */
  contact_count: number;
  /** Max ``CarrierContact.last_touchpoint_at`` across active contacts
   * at this carrier. Null when no qualifying touchpoint exists. */
  last_touchpoint_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}


export interface CarrierListResponse {
  rows: CarrierListRow[];
  total: number;
}


export interface CarrierListFilters {
  /** Case-insensitive substring on ``Carrier.name``. */
  q?: string;
  /** Filter chip — single carrier_type. ``undefined`` = all types. */
  carrier_type?: CarrierType;
  /** Filter chip. Defaults to ``active`` on first load; ``all`` is
   * an FE-side concept (we omit the backend filter when set). */
  status?: CarrierStatus | "all";
}


export interface CarrierListQuery {
  filters: CarrierListFilters;
}


// ── Detail / mutation contracts ────────────────────────────────────────────

/** Detail-fetch shape. Currently identical to ``CarrierListRow``
 * because the BE returns the same JSON for ``GET /api/v1/carriers/{id}``
 * (sans the list-aggregates). Kept as a separate alias so the FE can
 * extend it later (e.g. embedded suggestions, related plans) without
 * churning callers. */
export type CarrierDetail = CarrierListRow;


export interface CarrierWriteInput {
  name: string;
  carrier_type: CarrierType;
  naic_code?: string | null;
  parent_carrier_id?: string | null;
  website?: string | null;
  logo_url?: string | null;
  status?: CarrierStatus;
  notes?: string | null;
}


export class CarrierNotFoundError extends Error {
  constructor(public readonly carrierId: string) {
    super(`Carrier not found: ${carrierId}`);
    this.name = "CarrierNotFoundError";
  }
}


// ── CarrierContact contracts ───────────────────────────────────────────────

export interface CarrierContact {
  carrier_contact_id: string;
  carrier_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  title: string | null;
  role_type: CarrierContactRoleType;
  territory: string | null;
  carrier_office: string | null;
  segment_focus: string | null;
  status: CarrierContactStatus;
  last_known_destination: string | null;
  last_touchpoint_at: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}


export interface CarrierContactListResponse {
  rows: CarrierContact[];
  total: number;
}


export interface CarrierContactWriteInput {
  first_name: string;
  last_name: string;
  role_type: CarrierContactRoleType;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  title?: string | null;
  territory?: string | null;
  carrier_office?: string | null;
  segment_focus?: string | null;
  status?: CarrierContactStatus;
  last_known_destination?: string | null;
  notes?: string | null;
}


// ── Touchpoint shape consumed by the Activity tab ──────────────────────────

export interface CarrierTouchpoint {
  touchpoint_id: string;
  carrier_contact_id: string;
  carrier_contact_name: string | null;
  kind: string;
  subject: string | null;
  occurred_at: string | null;
  status: string;
  created_by_id: string | null;
  created_by_display_name: string | null;
}


export interface CarrierTouchpointListResponse {
  rows: CarrierTouchpoint[];
  total: number;
}
